/**
 * CSV Export Utilities for Users
 */

import type { User } from '../types';
import {
  isPopulatedOrganisation,
  isPopulatedUsergroup,
  isPopulatedSensorgroup,
} from './typeGuards';

/**
 * Escape a value for CSV format
 * Includes protection against CSV injection attacks
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  let str = String(value);

  // Prevent CSV injection by prefixing formula characters with a single quote
  // These characters can trigger formula execution in Excel/Sheets
  if (
    str.startsWith('=') ||
    str.startsWith('+') ||
    str.startsWith('-') ||
    str.startsWith('@') ||
    str.startsWith('\t') ||
    str.startsWith('\r')
  ) {
    str = "'" + str;
  }

  // If the value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * Get organisation names from user data
 */
function getOrganisationNames(user: User): string {
  if (!user.organisations || user.organisations.length === 0) return '';

  return user.organisations
    .map((op) => {
      if (!op.organisation) return null;
      if (typeof op.organisation === 'string') return op.organisation;
      if (isPopulatedOrganisation(op.organisation)) return op.organisation.name;
      return null;
    })
    .filter((name): name is string => name !== null)
    .join('; ');
}

/**
 * Get usergroup names from user data
 */
function getUsergroupNames(user: User): string {
  if (user.usergroups && user.usergroups.length > 0) {
    return user.usergroups
      .filter(isPopulatedUsergroup)
      .map((ug) => ug.name)
      .join('; ');
  }

  if (!user.organisations) return '';

  const allUsergroups: string[] = [];
  for (const op of user.organisations) {
    if (op.usergroups) {
      for (const ug of op.usergroups) {
        if (isPopulatedUsergroup(ug)) {
          allUsergroups.push(ug.name);
        }
      }
    }
  }

  return allUsergroups.join('; ');
}

/**
 * Get sensorgroup names from user data
 */
function getSensorgroupNames(user: User): string {
  if (user.sensorgroups && user.sensorgroups.length > 0) {
    return user.sensorgroups
      .filter(isPopulatedSensorgroup)
      .map((sg) => sg.name)
      .join('; ');
  }

  if (!user.organisations) return '';

  const allSensorgroups: string[] = [];
  for (const op of user.organisations) {
    if (op.sensorgroups) {
      for (const sg of op.sensorgroups) {
        if (isPopulatedSensorgroup(sg)) {
          allSensorgroups.push(sg.name);
        }
      }
    }
  }

  return allSensorgroups.join('; ');
}

/**
 * Format date for CSV
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString();
}

/**
 * Column configuration for users CSV export
 */
const USER_CSV_COLUMNS = [
  { key: 'firstname', label: 'First Name', getValue: (u: User) => u.firstname || '' },
  { key: 'lastname', label: 'Last Name', getValue: (u: User) => u.lastname || '' },
  { key: 'email', label: 'Email', getValue: (u: User) => u.email },
  { key: 'phone', label: 'Phone', getValue: (u: User) => u.phone || '' },
  { key: 'extension', label: 'Phone Extension', getValue: (u: User) => u.extension || '' },
  { key: 'mobile', label: 'Mobile', getValue: (u: User) => u.mobile || '' },
  { key: 'company', label: 'Company', getValue: (u: User) => u.company || '' },
  { key: 'position', label: 'Position', getValue: (u: User) => u.position || '' },
  { key: 'organisations', label: 'Organisations', getValue: (u: User) => getOrganisationNames(u) },
  { key: 'usergroups', label: 'User Groups', getValue: (u: User) => getUsergroupNames(u) },
  { key: 'sensorgroups', label: 'Sensor Groups', getValue: (u: User) => getSensorgroupNames(u) },
  { key: 'login_at', label: 'Last Login', getValue: (u: User) => formatDate(u.login_at) },
  { key: 'enabled', label: 'Status', getValue: (u: User) => (u.enabled ? 'Enabled' : 'Disabled') },
  { key: 'language', label: 'Language', getValue: (u: User) => (u.language === 'fr' ? 'French' : 'English') },
  { key: 'created_at', label: 'Created', getValue: (u: User) => formatDate(u.created_at) },
];

/**
 * Convert users to CSV string
 */
export function convertUsersToCSV(users: User[]): string {
  if (!users || users.length === 0) {
    return '';
  }

  // Create header row
  const header = USER_CSV_COLUMNS.map((col) => escapeCSVValue(col.label)).join(',');

  // Create data rows
  const rows = users.map((user) =>
    USER_CSV_COLUMNS.map((col) => escapeCSVValue(col.getValue(user))).join(',')
  );

  return [header, ...rows].join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export users to CSV and trigger download
 */
export function exportUsersToCSV(users: User[], filename: string = 'users'): void {
  if (!users || users.length === 0) {
    throw new Error('No users to export');
  }

  const csvContent = convertUsersToCSV(users);
  downloadCSV(csvContent, filename);
}
